import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AdminGuard, AdminTitle } from "@/components/admin/AdminGuard";
import { errText, getSettings, getSizeCharts } from "@/lib/api";
import { adminSaveSettings, adminSaveSizeChart } from "@/lib/admin";
import { resetSizeChartCache } from "@/components/SizeGuide";

const FIELDS = [["instagram", "Instagram URL", "https://instagram.com/…"], ["tiktok", "TikTok URL", "https://tiktok.com/@…"], ["youtube", "YouTube URL", "https://youtube.com/@…"], ["pinterest", "Pinterest URL", "https://pinterest.com/…"], ["contact_email", "Contact email", "hello@…"]];

const SettingsForm = () => {
  const [f, setF] = useState(null);
  useEffect(() => { getSettings().then(setF).catch((e) => toast.error(errText(e))); }, []);
  if (!f) return null;
  const save = async (e) => {
    e.preventDefault();
    try { setF(await adminSaveSettings(f)); toast.success("Settings saved. Refresh the site to see them."); } catch (err) { toast.error(errText(err)); }
  };
  return (
    <form onSubmit={save} className="mt-8 grid gap-6 md:grid-cols-2" data-testid="settings-form">
      {FIELDS.map(([k, l, ph]) => (
        <label key={k}><span className="label text-ink/60">{l}</span><input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} className="field" data-testid={`settings-${k}`} /></label>
      ))}
      <p className="text-[13px] text-ink/60 md:col-span-2">Empty fields hide that icon or link everywhere (footer, mobile menu, lookbook strip, Organization sameAs).</p>
      <div className="md:col-span-2"><button className="btn btn-lime" data-testid="settings-save">Save settings</button></div>
    </form>
  );
};

const ChartEditor = ({ cat, chart, onSaved }) => {
  const [c, setC] = useState(chart);
  const setRow = (i, k, v) => setC({ ...c, rows: c.rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  const save = async () => {
    try {
      const r = await adminSaveSizeChart(cat, { ...c, rows: c.rows.map((r) => ({ size: r.size, chest: Number(r.chest), length: Number(r.length), sleeve: Number(r.sleeve) })) });
      setC(r); resetSizeChartCache(); onSaved(); toast.success(`${cat} chart saved`);
    } catch (e) { toast.error(errText(e)); }
  };
  return (
    <div className="border-b border-ink/10 py-6" data-testid={`chart-${cat}`}>
      <p className="label font-bold">{cat}</p>
      {c.rows.length > 0 ? (
        <table className="mt-3 text-sm"><thead><tr className="label text-ink/60"><th className="pr-4 text-left">Size</th><th>Chest cm</th><th>Length cm</th><th>Sleeve cm</th></tr></thead>
          <tbody>{c.rows.map((r, i) => (
            <tr key={r.size}><td className="pr-4 font-mono">{r.size}</td>{["chest", "length", "sleeve"].map((k) => <td key={k} className="px-1"><input type="number" step="0.5" value={r[k]} onChange={(e) => setRow(i, k, e.target.value)} className="field !w-20 !py-1 text-center font-mono" data-testid={`chart-${cat}-${r.size}-${k}`} /></td>)}</tr>
          ))}</tbody></table>
      ) : (
        <label className="mt-3 block"><span className="label text-ink/60">Dimensions</span><input value={c.dimensions} onChange={(e) => setC({ ...c, dimensions: e.target.value })} className="field" data-testid={`chart-${cat}-dimensions`} /></label>
      )}
      <label className="mt-3 block"><span className="label text-ink/60">Fit note</span><input value={c.fit_note} onChange={(e) => setC({ ...c, fit_note: e.target.value })} className="field" data-testid={`chart-${cat}-fit`} /></label>
      <button onClick={save} className="btn btn-ink mt-4 !h-10" data-testid={`chart-${cat}-save`}>Save {cat}</button>
    </div>
  );
};

const Panel = () => {
  const [charts, setCharts] = useState(null);
  const load = () => getSizeCharts().then(setCharts).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <>
      <AdminTitle>Site settings</AdminTitle>
      <SettingsForm />
      <h2 className="display mt-16 text-4xl">Size charts</h2>
      <div className="mt-2">{charts && Object.entries(charts).map(([cat, c]) => <ChartEditor key={cat} cat={cat} chart={c} onSaved={load} />)}</div>
    </>
  );
};

export default function AdminSettings() {
  return <AdminGuard><Panel /></AdminGuard>;
}
